import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import artifactComposerCopy from '@/i18n/ns/artifact-composer';
import type { CheckedVM } from './view-models/types.ts';
import './composer/composer.css';

// The Checked card (F19), the content of BuildReveal's summary slot. It renders
// ART's projection of INS's revision-bound receipt and nothing else: a card exists
// only for a ready VM, and "passed" is shown only when the VM says passed. Build
// completion never produces one, and an absent receipt says so rather than implying
// a pass. DES2 animates the slot; this card's content is never animated.
//
// Tones (DES1): passed is success, did-not-pass is danger, could-not-run is the warning
// tone (it is not a failure of the Artifact), the rest are muted; all from theme tokens
// that switch in dark. Text that comes from the receipt (what failed, which views were
// checked) arrives as props and is placed as given; none of it is composed here.
export function CheckedCard({
  vm,
  versionLabel,
  resolveKey = (key) => key,
  coverageLine,
  failureLine,
  onCheck,
}: {
  vm: CheckedVM;
  /** The version as the version chip shows it, from ART's version projection. */
  versionLabel?: string;
  resolveKey?: (key: string) => string;
  /** From the receipt's views, e.g. "Checked at desktop and phone, light and dark". */
  coverageLine?: string | null;
  /** From the receipt, what did not pass, e.g. "2 numbers didn't match". */
  failureLine?: string | null;
  /** Runs the check; offered only when the viewer may, so it is only passed then. */
  onCheck?: () => void;
}) {
  const copy = useNamespaceCopy(artifactComposerCopy);
  if (vm.state.status !== 'ready') return null;
  const data = vm.state.data;
  // Stale beats the VM's own freshness: a card for an older revision is never green.
  const status = vm.state.freshness === 'stale' && data.status === 'passed' ? 'stale' : data.status;
  const headline = {
    passed: copy.checkedPassed,
    failed: copy.checkedFailed,
    blocked: copy.checkedBlocked,
    stale: copy.checkedStale,
    absent: copy.checkedAbsent,
  }[status];
  // Not checked yet has nothing to show beyond its status: no coverage, no limits.
  const evidence = status !== 'absent';
  const again = status === 'failed' || status === 'blocked' || status === 'stale';
  return (
    <section
      className="ws-checked"
      data-status={status}
      aria-label={copy.checkedTitle}
      data-receipt={data.receiptRef ?? undefined}
    >
      <header>
        <h3>{copy.checkedTitle}</h3>
        {versionLabel && (
          <span className="version">
            {formatCopy(copy.checkedVersion, { version: versionLabel })}
          </span>
        )}
      </header>
      <p className="ws-checked-status" role="status">
        {status === 'passed' && <span aria-hidden="true">✓ </span>}
        {headline}
      </p>
      {evidence && status === 'failed' && failureLine && (
        <p className="ws-checked-detail">{failureLine}</p>
      )}
      {evidence && coverageLine && <p className="ws-checked-screens">{coverageLine}</p>}
      {evidence && data.limitations.length > 0 && (
        <>
          <h4>{copy.checkedLimitations}</h4>
          <ul>
            {data.limitations.map((limitation) => (
              <li key={limitation}>{resolveKey(limitation)}</li>
            ))}
          </ul>
        </>
      )}
      {onCheck && (status === 'absent' || again) && (
        <p className="ws-checked-actions">
          <button type="button" className="ws-picker-trigger" onClick={onCheck}>
            {status === 'absent' ? copy.checkNow : copy.checkAgain}
          </button>
        </p>
      )}
    </section>
  );
}
